import React, { useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { MdKeyboardBackspace } from "react-icons/md";
import { useLazyOrderBySlugQuery } from "../../../../utils/api/order/order.api";
import { formatCurrency, getImage } from "../../../../utils/Utils";
import { getVendeur } from "../../../HomePage/HomePage";
import { useGetListProductQuery } from "../../../../utils/api/product/product.api";
import Empty from "../../../../assets/images/appImages/category.png";
import Skeleton from "react-loading-skeleton";
import { AlertInfo } from "../../../common/Alert";

function CommandeDetail() {
  const Vendeur = getVendeur();
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const [findBySlug, { data, isLoading }] = useLazyOrderBySlugQuery();
  const { data: productsData } = useGetListProductQuery();

  useEffect(() => {
    if (slug) {
      findBySlug(slug as string);
    }
  }, [slug]);

  // Calculer le sous-total (somme des produits)
  const subtotal = useMemo(() => {
    if (!data?.produits) return 0;
    return data.produits.reduce((sum: number, item: any) => {
      return sum + (parseFloat(item.prix) || 0) * (parseFloat(item.quantite) || 0);
    }, 0);
  }, [data?.produits]);

  // Calculer les frais de livraison (différence entre total et sous-total)
  const deliveryFees = useMemo(() => {
    const total = parseFloat(data?.montant_total) || 0;
    return Math.max(0, total - subtotal);
  }, [data?.montant_total, subtotal]);

  const getPaymentMethod = () => {
    if (!data?.mode_de_paiement) return "Non spécifié";
    switch (data.mode_de_paiement) {
      case "ORANGE_SN_API_CASH_OUT":
        return "Orange Money";
      case "WAVE_SN_API_CASH_OUT":
        return "Wave";
      case "CASH":
        return "Paiement à la livraison";
      case "BANK_TRANSFER_SN_API_CASH_OUT":
        return "Virement Bancaire";
      default:
        return data.mode_de_paiement;
    }
  };

  const getOrderStatus = () => {
    if (!data?.statut) return "Non spécifié";
    switch (data.statut) {
      case "EN_COURS_DE_TRAITEMENT":
        return "En cours de traitement";
      case "ANNULE":
        return "Annulée";
      case "EN_COURS_DE_LIVRAISON":
        return "En cours de livraison";
      case "LIVRE":
        return "Livrée";
      default:
        return data.statut;
    }
  };

  const getProductImage = (item: any) => {
    const produit = productsData?.results?.find((x: any) => x.id == item?.produit?.id);
    if (!produit) return Empty;
    
    if (produit.type === "taille_unique") {
      return produit.images?.[0]?.image || Empty;
    }
    
    return item?.variations?.[0]?.images?.[0]?.image || 
           produit?.variations?.[0]?.images?.[0]?.image || 
           Empty;
  };

  const getProductName = (item: any) => {
    const produit = productsData?.results?.find((x: any) => x.id == item?.produit?.id);
    return produit?.nom || "Produit inconnu";
  };

  return (
    <div className="order-detail-page">
      <div className="order-detail-header">
        <button className="order-detail-back-btn" onClick={() => navigate(-1)}>
          <MdKeyboardBackspace />
          <span>Retour</span>
        </button>
      </div>

      <div className="order-detail-content">
        {/* Section Produits */}
        <div className="order-detail-section">
          <div className="order-detail-section-title">Produits</div>
          {isLoading ? (
            <Skeleton height={200} />
          ) : !data?.produits || data.produits.length === 0 ? (
            <AlertInfo message="Aucun produit trouvé" />
          ) : (
            <div className="order-products-table">
              <div className="order-products-header">
                <div className="order-products-col product-col">Produits</div>
                <div className="order-products-col quantity-col">Quantité</div>
                <div className="order-products-col price-col">Prix</div>
                <div className="order-products-col total-col">Total</div>
              </div>
              <div className="order-products-body">
                {data.produits.map((item: any, index: number) => {
                  const productTotal = (parseFloat(item.prix) || 0) * (parseFloat(item.quantite) || 0);
                  return (
                    <div key={item.id || index} className="order-product-row">
                      <div className="order-products-col product-col">
                        <div className="order-product-info">
                          <img
                            src={getImage(getProductImage(item))}
                            alt={getProductName(item)}
                            className="order-product-image"
                          />
                          <span className="order-product-name">{getProductName(item)}</span>
                        </div>
                      </div>
                      <div className="order-products-col quantity-col">
                        <span>{item.quantite || 0}</span>
                      </div>
                      <div className="order-products-col price-col">
                        <span>{formatCurrency(item.prix || 0, data?.devise || Vendeur?.devise || "XOF")}</span>
                      </div>
                      <div className="order-products-col total-col">
                        <span>{formatCurrency(productTotal, data?.devise || Vendeur?.devise || "XOF")}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="order-detail-separator"></div>

        {/* Section Mode de paiement */}
        <div className="order-detail-section">
          <div className="order-detail-section-title">Mode de paiement</div>
          <div className="order-detail-section-value">
            {isLoading ? <Skeleton width={150} /> : getPaymentMethod()}
          </div>
        </div>

        <div className="order-detail-separator"></div>

        {/* Section Statut de la commande */}
        <div className="order-detail-section">
          <div className="order-detail-section-title">Statut de la commande</div>
          <div className="order-detail-section-value">
            {isLoading ? <Skeleton width={150} /> : getOrderStatus()}
          </div>
        </div>

        <div className="order-detail-separator"></div>

        {/* Section Order Summary */}
        <div className="order-summary-section">
          <div className="order-summary-item">
            <span className="order-summary-label">Prix sous-total:</span>
            <span className="order-summary-value">
              {isLoading ? (
                <Skeleton width={100} />
              ) : (
                formatCurrency(subtotal, data?.devise || Vendeur?.devise || "XOF")
              )}
            </span>
          </div>
          <div className="order-summary-item">
            <span className="order-summary-label">Frais de livraison (+):</span>
            <span className="order-summary-value">
              {isLoading ? (
                <Skeleton width={100} />
              ) : (
                formatCurrency(deliveryFees, data?.devise || Vendeur?.devise || "XOF")
              )}
            </span>
          </div>
          <div className="order-summary-item order-summary-total">
            <span className="order-summary-label">Total:</span>
            <span className="order-summary-value">
              {isLoading ? (
                <Skeleton width={100} />
              ) : (
                formatCurrency(data?.montant_total || 0, data?.devise || Vendeur?.devise || "XOF")
              )}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CommandeDetail;
